import type { ReactNode } from 'react';

/*
  The sign-in providers, in the order the pages offer them. Shared so the
  sign-in page and the registration form cannot drift apart: both show the same
  marks, the same names, and the same nudge for an address the provider owns.

  Both marks are the official artwork in the official colours — Google's brand
  terms require the four colours unaltered, Microsoft's require the four squares.
*/
export interface SsoProvider {
  id: string;
  label: string;
  /*
    Addresses this provider owns. Used only to suggest: it is a guess from what
    was typed and never asks the server anything, so it cannot reveal whether an
    account exists — and a Gmail address may still have a password here.
  */
  ownsAddress: RegExp;
  icon: ReactNode;
}

export const SSO: readonly SsoProvider[] = [
  {
    id: 'google',
    label: 'Google',
    ownsAddress: /@(gmail|googlemail)\.com\s*$/i,
    icon: (
      <svg aria-hidden viewBox="0 0 18 18" className="h-[18px] w-[18px] shrink-0">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33Z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58Z" />
      </svg>
    ),
  },
  {
    id: 'microsoft',
    label: 'Microsoft',
    ownsAddress: /@(outlook|hotmail|live|msn)\.[a-z.]+\s*$/i,
    icon: (
      <svg aria-hidden viewBox="0 0 18 18" className="h-[18px] w-[18px] shrink-0">
        <path fill="#F25022" d="M0 0h8.55v8.55H0Z" />
        <path fill="#7FBA00" d="M9.45 0H18v8.55H9.45Z" />
        <path fill="#00A4EF" d="M0 9.45h8.55V18H0Z" />
        <path fill="#FFB900" d="M9.45 9.45H18V18H9.45Z" />
      </svg>
    ),
  },
];

/**
 * The provider whose own addresses this one looks like, if that provider is
 * configured. Undefined for everything else — a Yahoo or church-domain address
 * has no provider to be sent to.
 */
export const providerFor = (email: string, enabled: string[]): SsoProvider | undefined =>
  SSO.find((p) => enabled.includes(p.id) && p.ownsAddress.test(email));

/** Where the browser goes to start a handshake. A full-page redirect, never fetch. */
export const ssoHref = (id: string, returnTo?: string): string =>
  `/api/auth/${id}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`;

/**
 * The amber note that appears under an email field when the address belongs to
 * a provider. Amber rather than red on every page that uses it: it is an
 * instruction to follow, not a mistake to fix.
 */
export function SsoNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 rounded-lg border border-gold-200 bg-gold-100 px-3 py-2 text-sm font-medium text-gold-700">
      {children}
    </p>
  );
}

/** The link inside that note, styled to read as part of the sentence. */
export function SsoLink({ provider }: { provider: SsoProvider }) {
  return (
    <a href={ssoHref(provider.id)} className="underline underline-offset-2">
      Continue with {provider.label}
    </a>
  );
}
